import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { jwtDecode } from "jwt-decode";
import { refreshToken } from "./authService";

const API_BASE_URL = process.env.REACT_APP_API_URL + "/api";

const apiClient = axios.create({
  baseURL: API_BASE_URL, // Use your base URL here
  headers: {
    "X-API-Key": process.env.REACT_APP_API_KEY, // Use your API key here
  },
});

//persist the access token in memory to make the application more secure, and keep refresh in local storage
//The main idea behind this split approach is to minimize the exposure of the more powerful,
//long-lived refresh token while still providing a smooth user experience.
let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

// Add a request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Check if headers exist and if requiresAuth is set
    if (config.headers?.get("requiresAuth") === "true" && accessToken) {
      config.headers.set("Authorization", `Bearer ${accessToken}`);
    }

    // Remove the custom header to avoid sending it to the server
    if (config.headers) {
      config.headers.delete("requiresAuth");
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

//Add a response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      originalRequest.headers?.get("requiresAuth") === "true"
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const response = await apiClient.post("/auth/refresh-token", {
          refreshToken,
        });
        const { accessToken: newAccessToken } = response.data;
        setAccessToken(newAccessToken);

        // Set the new access token in the Authorization header
        originalRequest.headers.set(
          "Authorization",
          `Bearer ${newAccessToken}`
        );
        return apiClient(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem("refreshToken");
        setAccessToken(null);
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Helper function to determine if a request requires authentication
export const requiresAuth = (
  config?: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (config?.headers) {
    config.headers.set("requiresAuth", "true");
  } else {
    config = { headers: new AxiosHeaders({ requiresAuth: "true" }) };
  }
  return config;
};
